import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../common/Button';
import { endSessionClassroom, classroomMeetingPath } from '../../api/classroom';
import { getApiErrorInfo } from '../../utils/apiError';
import { useCurrentUser } from '../../context/UserContext';
import { getNormalizedRole } from '../../utils/auth';
import { resolveClassroomPortalRole } from '../../utils/classroomPortalRole';

const HOST_SLOT = 'min-w-[8.5rem] justify-center';
const END_SLOT = 'min-w-[5.75rem] justify-center';
const HOST_PLACEHOLDER =
  'inline-flex items-center justify-center min-w-[8.5rem] px-3 py-1.5 text-sm font-medium rounded-lg invisible pointer-events-none select-none';
const END_PLACEHOLDER =
  'inline-flex items-center justify-center min-w-[5.75rem] px-3 py-1.5 text-sm font-medium rounded-lg invisible pointer-events-none select-none';

/**
 * Staff Join Session (in-app embed) + End class.
 * Meeting create/start happens on the meeting page via embed-auth.
 */
export default function SessionHostAction({
  sessionId,
  role: roleProp,
  classroomEligible = true,
  hasMeeting = false,
  meetingStatus,
  status,
  endTime,
  className = '',
  compact = false,
  aligned = false,
  showEnd = true,
  onStarted,
  onEnded,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [busy, setBusy] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState('');

  const portalRole = resolveClassroomPortalRole(roleProp, user, getNormalizedRole())
    || String(getNormalizedRole() || '').toUpperCase().replace(/^ROLE_/, '')
    || 'COACH';
  const reserveSlots = aligned === true;

  const st = String(status || '').toUpperCase();
  const ms = String(meetingStatus || '').toUpperCase();
  const cancelled = st === 'CANCELLED';
  const completed = st === 'COMPLETED';
  const ended = ms === 'ENDED';
  const creating = ms === 'CREATING';
  const failed = ms === 'FAILED';
  const liveMeeting = hasMeeting && !ended && !failed && (ms === 'STARTED' || ms === 'CREATED' || ms === '');
  const endMs = endTime ? new Date(endTime).getTime() : NaN;
  const pastSessionEnd = Number.isFinite(endMs) && endMs < Date.now();
  const withinStartWindow = liveMeeting || !pastSessionEnd;

  const canHost = Boolean(
    sessionId
    && !cancelled
    && (!completed || hasMeeting)
    && (hasMeeting || classroomEligible !== false)
    && withinStartWindow,
  );

  const joinMeetingLabel = t('classroom.live.joinSession', { defaultValue: 'Join Session' });
  const startLabel = busy || creating
    ? t('classroom.live.starting', { defaultValue: 'Opening…' })
    : joinMeetingLabel;

  const handleStart = () => {
    if (creating || !sessionId) return;
    setBusy(true);
    setError('');
    try {
      onStarted?.(sessionId);
      navigate(classroomMeetingPath(portalRole, sessionId, { host: true }));
    } catch (err) {
      setError(getApiErrorInfo(err, t('classroom.live.startFailed')).message);
      setBusy(false);
    }
  };

  const handleEnd = async () => {
    const ok = window.confirm(
      t('classroom.meeting.endHostConfirm', {
        defaultValue: 'End class for everyone? Students will be removed from the Zoom meeting.',
      }),
    );
    if (!ok) return;
    setEnding(true);
    setError('');
    try {
      await endSessionClassroom(sessionId);
      onEnded?.(sessionId);
    } catch (err) {
      setError(getApiErrorInfo(err, t('classroom.live.endFailed')).message);
    } finally {
      setEnding(false);
    }
  };

  const endPlaceholder = showEnd ? (
    <span className={END_PLACEHOLDER} aria-hidden="true">
      {t('classroom.live.end')}
    </span>
  ) : null;

  const hostPlaceholder = (
    <span className={HOST_PLACEHOLDER} aria-hidden="true">
      {t('classroom.live.joinSession', { defaultValue: 'Join Session' })}
    </span>
  );

  if (!canHost) {
    if (!reserveSlots) {
      return null;
    }
    return (
      <div className={`inline-flex flex-col items-stretch gap-1 ${className}`}>
        <div className="inline-flex items-center gap-2">
          {hostPlaceholder}
          {endPlaceholder}
        </div>
      </div>
    );
  }

  const hostButton = compact ? (
    <button
      type="button"
      className={`btn-action-edit ${HOST_SLOT}`}
      disabled={busy || ending || creating}
      onClick={handleStart}
      title={startLabel}
      data-classroom-start
    >
      <svg className="w-4 h-4 mr-1 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
      <span className="truncate">{startLabel}</span>
    </button>
  ) : (
    <Button
      type="button"
      variant="page-primary"
      disabled={busy || ending || creating}
      onClick={handleStart}
      title={startLabel}
      data-classroom-start
      className={reserveSlots ? 'min-w-[7.25rem]' : undefined}
    >
      {startLabel}
    </Button>
  );

  const endButton = showEnd && liveMeeting
    ? (compact ? (
      <button
        type="button"
        className={`btn-action-archive ${END_SLOT}`}
        disabled={busy || ending}
        onClick={handleEnd}
        title={t('classroom.live.end')}
        data-classroom-end
      >
        {ending ? t('classroom.live.ending') : t('classroom.live.end')}
      </button>
    ) : (
      <Button
        type="button"
        variant="page-secondary"
        disabled={busy || ending}
        onClick={handleEnd}
        data-classroom-end
        className={reserveSlots ? 'min-w-[5.75rem]' : undefined}
      >
        {ending ? t('classroom.live.ending') : t('classroom.live.end')}
      </Button>
    ))
    : (reserveSlots ? endPlaceholder : null);

  return (
    <div className={`inline-flex flex-col items-stretch gap-1 ${className}`}>
      <div className={`inline-flex items-center gap-2 ${reserveSlots ? '' : 'flex-wrap'}`}>
        {hostButton}
        {endButton}
      </div>
      {error ? (
        <p className="text-xs text-red-700 max-w-[16rem] text-left" role="alert">{error}</p>
      ) : null}
    </div>
  );
}
